import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Field as AkField, ErrorMessage } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Condition } from '../../../../../../../../../common/types';
import messages from './messages';
import { validateConditions } from './utils';

const SelectedConditionsView = ({
	intl: { formatMessage },
	conditions,
	onRemove,
	listName,
}: Intl & {
	conditions: Condition[];
	onRemove: (conditionId: string) => void;
	listName: string;
}) => (
	<AkField
		name={listName}
		defaultValue={conditions}
		// @ts-expect-error - TS2322 - Type '(value: Condition[] | undefined) => boolean | undefined' is not assignable to type '(value: Condition[] | undefined, formState: Object, fieldState: Meta) => string | void | Promise<string | void>'.
		validate={(value) => validateConditions(value, listName)}
	>
		{({ fieldProps: { value }, error }) => (
			<>
				{error !== undefined && (
					<ErrorMessage>{formatMessage(messages.validateMessage)}</ErrorMessage>
				)}
				{value.map((condition) => {
					const { conditionId, name } = condition;
					return (
						<Column
							key={`${listName}-condition-${condition.conditionId}`}
							tabIndex={0}
							data-test-id={`servicedesk-sla-settings.ui.metrics.metric.editable.form.conditions.condition.fields.selected-condition-${listName}-${conditionId}`}
						>
							{name}
							<RemoveConditionButton
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className="remove-condition-button"
								appearance="subtle"
								onClick={() => onRemove(conditionId)}
								iconAfter={
									<CrossIcon size="small" label={formatMessage(messages.removeCondition)} />
								}
								data-test-id={`servicedesk-sla-settings.ui.metrics.metric.editable.form.conditions.condition.fields.selected-condition.remove-condition-button-${listName}-${conditionId}`}
							/>
						</Column>
					);
				})}
			</>
		)}
	</AkField>
);

export const SelectedConditions = injectIntl(SelectedConditionsView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const RemoveConditionButton = styled(Button)({
	position: 'absolute',
	left: '-10000px',
	top: 'auto',
	overflow: 'hidden',
	width: '24px',
	height: '24px',
	'&:focus, &:hover': {
		position: 'static',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Column = styled.div({
	boxSizing: 'border-box',
	padding: `0 ${token('space.100', '8px')}`,
	width: '100%',
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px 0 rgba(9, 30, 66, 0.25), 0 0 1px 0 rgba(9, 30, 66, 0.31)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	alignItems: 'center',
	justifyContent: 'space-between',
	display: 'flex',
	marginBottom: token('space.100', '8px'),

	'&:hover, &:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& .remove-condition-button': {
			position: 'static',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.raised', colors.N0),
	borderRadius: token('space.050', '4px'),
});
